<template>
    <v-card class="round-card background-2 mb-4">
        <v-card-title class="pa-3">
            <v-icon color="primary" class="mr-3">mdi-timeline</v-icon>
            <p>Timeline Ticket</p>
            <v-spacer></v-spacer>
            <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                    <v-btn icon v-on="on" @click="scrollToBottom">
                        <v-icon>mdi-arrow-down-bold</v-icon>
                    </v-btn>
                </template>
                <span>Vai in fondo</span>
            </v-tooltip>
            <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                    <v-btn icon v-on="on" @click="toggleCollapseAll">
                        <v-icon>{{ allCollapsed ? 'mdi-unfold-more-horizontal' : 'mdi-unfold-less-horizontal' }}</v-icon>
                    </v-btn>
                </template>
                <span>{{ allCollapsed ? 'Espandi tutti' : 'Comprimi tutti' }}</span>
            </v-tooltip>
            <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                    <v-btn icon v-on="on" @click="$emit('refresh')" :loading="loading">
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                </template>
                <span>Aggiorna</span>
            </v-tooltip>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text v-if="loading" class="text-center pa-8">
            <v-progress-circular
                indeterminate
                color="primary"
            ></v-progress-circular>
        </v-card-text>

        <v-card-text v-else-if="(!timeline || timeline.length === 0) && (!ticket || !ticket.request)" class="text-center pa-8 grey--text">
            <v-icon large class="mb-2">mdi-timeline-outline</v-icon>
            <div>Nessun elemento nella timeline</div>
        </v-card-text>

        <v-card-text v-else class="pa-0">
            <v-timeline dense class="pt-4">
                <v-timeline-item
                    v-for="item in timeline"
                    :key="`${item.type}-${item.id}`"
                    :color="getItemColor(item)"
                    :icon="getItemIcon(item)"
                    fill-dot
                >
                    <!-- Maillog Item -->
                    <maillog-timeline-item
                        v-if="item.type === 'maillog'"
                        :maillog="item.data"
                        :collapsed="isCollapsed(item)"
                        @toggle="toggleCollapse(item)"
                    ></maillog-timeline-item>

                    <!-- Comment Item (Nota Privata / Attività) -->
                    <comment-timeline-item
                        v-if="item.type === 'comment'"
                        :comment="item.data"
                        :collapsed="isCollapsed(item)"
                        @toggle="toggleCollapse(item)"
                        @update="updateComment"
                        @delete="deleteComment"
                    ></comment-timeline-item>

                    <!-- Forward Item (email inoltrata) -->
                    <forward-timeline-item
                        v-if="item.type === 'forward'"
                        :comment="item.data"
                        :collapsed="isCollapsed(item)"
                        @toggle="toggleCollapse(item)"
                    ></forward-timeline-item>

                    <!-- Intervention Item (con eventuale maillog) -->
                    <intervention-timeline-item
                        v-if="item.type === 'intervention'"
                        :intervention="item.data"
                        :maillog="item.maillog"
                        :subtype="item.subtype"
                        :collapsed="isCollapsed(item)"
                        @toggle="toggleCollapse(item)"
                        @edit="editIntervention"
                        @refresh="$emit('refresh')"
                    ></intervention-timeline-item>
                </v-timeline-item>

                <!-- Box Richiesta Originale (sempre in fondo alla timeline) -->
                <v-timeline-item
                    v-if="ticket && ticket.request"
                    color="info"
                    icon="mdi-help-circle"
                    fill-dot
                >
                    <v-card class="elevation-1 round-card background-1 mr-4">
                        <v-card-title class="pa-3 pb-1">
                            <div class="d-flex align-center flex-wrap" style="width: 100%">
                                <v-avatar color="info" size="32" class="mr-2 d-flex align-center justify-center">
                                    <v-icon dark class="ma-0">mdi-help-circle</v-icon>
                                </v-avatar>
                                <div class="flex-grow-1">
                                    <div class="subtitle-1 font-weight-medium" style="line-height: 1">
                                        Richiesta Originale
                                    </div>
                                    <div class="caption grey--text">
                                        Apertura ticket
                                    </div>
                                </div>
                                <div class="caption grey--text text-right d-flex align-center">
                                    <v-chip x-small class="mr-2" color="info" dark>
                                        <v-icon x-small left>mdi-help-circle</v-icon>
                                        Richiesta
                                    </v-chip>
                                    {{ formatDate(ticket.created_at) }}
                                </div>
                            </div>
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-card-text class="pa-3 body-2">
                            <div v-html="sanitizeHtml(ticket.request)"></div>
                        </v-card-text>
                    </v-card>
                </v-timeline-item>
            </v-timeline>
        </v-card-text>

        <!-- Dialog conferma eliminazione commento -->
        <v-dialog v-model="deleteDialog" max-width="600">
            <v-card class="round-card background-2">
                <v-card-title>
                    <p>Conferma eliminazione</p>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="background-2 pa-6">
                    Sei sicuro di voler eliminare questo commento?
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelDeleteComment">Annulla</v-btn>
                    <v-btn text color="error" @click="confirmDeleteComment">Elimina</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import MaillogTimelineItem from './MaillogTimelineItem'
import CommentTimelineItem from './CommentTimelineItem'
import ForwardTimelineItem from './ForwardTimelineItem'
import InterventionTimelineItem from './InterventionTimelineItem'
import { sanitizeHtml } from '@/utils/sanitizeHtml'

export default {
    name: 'TroubleTimeline',

    components: {
        MaillogTimelineItem,
        CommentTimelineItem,
        ForwardTimelineItem,
        InterventionTimelineItem
    },

    props: {
        timeline: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        ticket: {
            type: Object,
            default: null
        }
    },

    data() {
        return {
            collapsedItems: {},
            deleteDialog: false,
            commentToDelete: null
        }
    },

    computed: {
        allCollapsed() {
            if (!this.timeline || this.timeline.length === 0) return false
            return this.timeline.every(item => this.isCollapsed(item))
        }
    },

    watch: {
        timeline: {
            handler() {
                // Quando cambia la timeline, mantieni il primo elemento espanso
                if (this.timeline && this.timeline.length > 0) {
                    this.collapsedItems = {}
                    this.timeline.slice(1).forEach(item => {
                        this.$set(this.collapsedItems, `${item.type}-${item.id}`, true)
                    })
                }
            },
            immediate: true
        }
    },

    methods: {
        sanitizeHtml,

        getItemColor(item) {
            switch (item.type) {
                case 'maillog':
                    return item.data?.direction === 'outbound' ? 'primary' : 'grey'
                case 'comment':
                    return item.data?.type === 'activity' ? 'blue-grey' : 'orange'
                case 'forward':
                    return 'purple'
                case 'intervention':
                    return this.getInterventionColor(item.subtype)
                default:
                    return 'grey'
            }
        },

        getInterventionColor(subtype) {
            const colors = {
                intervention: 'success',
                intervention_remote: 'info',
                trip: 'warning'
            }
            return colors[subtype] || 'success'
        },

        getItemIcon(item) {
            switch (item.type) {
                case 'maillog':
                    return item.data?.direction === 'outbound' ? 'mdi-email-fast-outline' : 'mdi-email-arrow-left-outline'
                case 'comment':
                    return item.data?.type === 'activity' ? 'mdi-history' : 'mdi-note-text'
                case 'forward':
                    return 'mdi-share'
                case 'intervention':
                    return this.getInterventionIcon(item.subtype)
                default:
                    return 'mdi-circle'
            }
        },

        getInterventionIcon(subtype) {
            const icons = {
                intervention: 'mdi-wrench',
                intervention_remote: 'mdi-remote-desktop',
                trip: 'mdi-car'
            }
            return icons[subtype] || 'mdi-wrench'
        },

        getItemKey(item) {
            return `${item.type}-${item.id}`
        },

        isCollapsed(item) {
            return !!this.collapsedItems[this.getItemKey(item)]
        },

        toggleCollapse(item) {
            const key = this.getItemKey(item)
            this.$set(this.collapsedItems, key, !this.collapsedItems[key])
        },

        toggleCollapseAll() {
            if (!this.timeline || this.timeline.length === 0) return

            if (this.allCollapsed) {
                // Espandi tutti
                this.collapsedItems = {}
            } else {
                // Collassa tutti
                this.timeline.forEach(item => {
                    this.$set(this.collapsedItems, this.getItemKey(item), true)
                })
            }
        },

        scrollToBottom() {
            this.$nextTick(() => {
                window.scrollTo({
                    top: document.body.scrollHeight,
                    behavior: 'smooth'
                })
            })
        },

        async updateComment({ id, text }) {
            try {
                await this.axios.put(`/comments/${id}`, { text, editable: true })
                this.$store.commit('setSnackbar', {
                    timeout: 3000,
                    text: 'Commento aggiornato',
                    value: true,
                    color: 'success'
                })
                this.$emit('refresh')
            } catch (error) {
                this.$store.commit('setSnackbarError')
            }
        },

        deleteComment(id) {
            this.commentToDelete = id
            this.deleteDialog = true
        },

        async confirmDeleteComment() {
            if (!this.commentToDelete) return

            try {
                await this.axios.post('/comments/delete', { ids: [this.commentToDelete] })
                this.$store.commit('setSnackbar', {
                    timeout: 3000,
                    text: 'Commento eliminato',
                    value: true,
                    color: 'success'
                })
                this.$emit('refresh')
            } catch (error) {
                this.$store.commit('setSnackbarError')
            } finally {
                this.deleteDialog = false
                this.commentToDelete = null
            }
        },

        cancelDeleteComment() {
            this.deleteDialog = false
            this.commentToDelete = null
        },

        formatDate(date) {
            if (!date) return ''
            return this.$format(new Date(date), 'dd/MM/yyyy HH:mm')
        },

        // Emette l'evento per modificare un intervento
        editIntervention(intervention) {
            this.$emit('edit-intervention', intervention)
        }
    }
}
</script>
